import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("wechr_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ---- Auth ----
export const authApi = {
  signup: (data) => api.post("/auth/signup", data),
  login: (data) => api.post("/auth/login", data),
  me: () => api.get("/auth/me"),
  googleSession: (session_id) => api.post("/auth/google/session", { session_id }),
  logout: () => api.post("/auth/logout"),
  verifyEmail: (token) => api.post("/auth/verify-email", { token }),
  resendVerification: (email) => api.post("/auth/resend-verification", { email }),
};

export const contactApi = {
  send: (data) => api.post("/contact", data),
};

// ---- Content ----
export const contentApi = {
  codeOfConduct: () => api.get("/content/code-of-conduct"),
  framework: () => api.get("/content/framework"),
  questionnaire: () => api.get("/content/questionnaire"),
  documentCategories: () => api.get("/content/document-categories"),
  pricing: () => api.get("/content/pricing"),
  formOptions: () => api.get("/content/form-options"),
  cocPdfUrl: () => `${API}/code-of-conduct.pdf`,
  checklists: () => api.get("/content/checklists"),
  checklistPdfUrl: (key) => `${API}/content/checklist/${key}.pdf`,
};

// ---- Applications ----
export const appApi = {
  create: (data) => api.post("/applications", data),
  list: () => api.get("/applications"),
  get: (id) => api.get(`/applications/${id}`),
  update: (id, data) => api.put(`/applications/${id}`, data),
  saveQuestionnaire: (id, answers) => api.put(`/applications/${id}/questionnaire`, { answers }),
  listDocuments: (id) => api.get(`/applications/${id}/documents`),
  uploadDocument: (id, category, file, evidenceType) => {
    const fd = new FormData();
    fd.append("category", category);
    if (evidenceType) fd.append("evidence_type", evidenceType);
    fd.append("file", file);
    return api.post(`/applications/${id}/documents`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deleteDocument: (id, docId) => api.delete(`/applications/${id}/documents/${docId}`),
  submit: (id) => api.post(`/applications/${id}/submit`),
  audit: (id) => api.get(`/applications/${id}/audit`),
  renew: (id) => api.post(`/applications/${id}/renew`),
  checkout: (id, originUrl, promoCode) => api.post(`/applications/${id}/checkout`, { origin_url: originUrl, promo_code: promoCode || null }),
  validatePromo: (id, code) => api.post(`/promo-codes/validate`, { application_id: id, code }),
};

// ---- Payments ----
export const paymentApi = {
  status: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Download a protected (auth-required) file as a blob and save it
export const downloadAuthedFile = async (path, filename) => {
  const res = await api.get(path, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ---- Admin ----
export const adminApi = {
  stats: () => api.get("/admin/stats"),
  listApplications: (status) => api.get("/admin/applications", { params: status ? { status } : {} }),
  getApplication: (id) => api.get(`/admin/applications/${id}`),
  adjustScores: (id, data) => api.put(`/admin/applications/${id}/scores`, data),
  decision: (id, data) => api.post(`/admin/applications/${id}/decision`, data),
  issueCertificate: (id, data) => api.post(`/admin/applications/${id}/issue-certificate`, data),
  certStatus: (certId, status) => api.post(`/admin/certificates/${certId}/status`, { status }),
  notifications: () => api.get("/admin/notifications"),
  expiringCerts: () => api.get("/admin/certificates/expiring"),
  sendReminder: (certId) => api.post(`/admin/certificates/${certId}/send-reminder`),
  runAiReview: (id) => api.post(`/applications/${id}/ai-review`),
  aiReviewStatus: (id) => api.get(`/applications/${id}/ai-review/status`),
  scorecardPdfPath: (id) => `/applications/${id}/scorecard.pdf`,
  docDownloadPath: (appId, docId) => `/admin/applications/${appId}/documents/${docId}/download`,
  docsZipPath: (appId) => `/admin/applications/${appId}/documents.zip`,
  // User account management
  listUsers: (params) => api.get("/admin/users", { params: params || {} }),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  // Promotion codes
  listPromoCodes: () => api.get("/admin/promo-codes"),
  createPromoCode: (data) => api.post("/admin/promo-codes", data),
  updatePromoCode: (id, data) => api.put(`/admin/promo-codes/${id}`, data),
  deletePromoCode: (id) => api.delete(`/admin/promo-codes/${id}`),
};

// ---- Certificates / verify ----
export const certApi = {
  get: (certId) => api.get(`/certificates/${certId}`),
  pdfUrl: (certId) => `${API}/certificates/${certId}/pdf`,
  verify: (q) => api.get("/verify", { params: { q } }),
};

export default api;
